import { colorTrack, presence, press, smooth, track } from './springs'

/**
 * Laufzeit von „Aivy Morph“. Aus einem Drehbuch (States, Cursor, Dehnung) und
 * den Szenen baut sie `seek(t)`: Kamera, Form, Inhalte und Cursor sind reine
 * Funktionen der Zeit. Inhalte blenden mit eigenem Timing und kurzem Blur.
 */

const TRAVEL = { w: 9, z: 0.92 }

const cameraFor = (w, h, W, H, fill, cap) =>
  Math.min((W * fill) / w, (H * fill) / h, cap)

/** Abschnitte je Inhalt: aufeinanderfolgende States mit gleichem Inhalt verschmelzen. */
function runsOf(states) {
  const runs = {}
  states.forEach((s, i) => {
    const end = i === states.length - 1 ? Infinity : states[i + 1].t
    const list = (runs[s.c] = runs[s.c] || [])
    if (i > 0 && states[i - 1].c === s.c) list[list.length - 1][1] = end
    else list.push([i === 0 ? -Infinity : s.t + 0.08, end])
  })
  return runs
}

function cursorTracks({ start, keys }) {
  const xs = []
  const ys = []
  keys.forEach((k) => {
    if (k.hold) {
      xs.push({ hold: k.hold, fn: k.fx })
      ys.push({ hold: k.hold, fn: k.fy })
      return
    }
    xs.push({ t: k[0], to: k[1], ...TRAVEL })
    ys.push({ t: k[0], to: k[2], ...TRAVEL })
  })
  return [track(start[0], xs), track(start[1], ys)]
}

export function createMorph(
  root,
  script,
  scenes,
  { cursorScale = 1, fill: fillOpt = 0.6, cap = 2.6 } = {}
) {
  const { DUR, STATES, CURSOR, STRETCH } = script
  const q = (sel) => root.querySelector(`[data-mo="${sel}"]`)
  const qa = (sel) => Array.from(root.querySelectorAll(`[data-mo="${sel}"]`))
  const world = q('world')
  const shape = q('shape')
  const cursor = q('cursor')
  const content = {}
  root.querySelectorAll('[data-c]').forEach((n) => {
    content[n.dataset.c] = n
  })
  const runs = runsOf(STATES)
  const updates = scenes.map((s) => ({
    ids: s.ids,
    update: s.build({ q, qa, runs })
  }))

  let built = null
  const build = () => {
    const W = root.clientWidth
    const H = root.clientHeight
    const fill = typeof fillOpt === 'function' ? fillOpt(W, H) : fillOpt
    const [first, ...rest] = STATES
    const ev = (key) => rest.map((s) => ({ t: s.t, to: s[key] }))
    const [cx, cy] = cursorTracks(CURSOR)
    built = {
      W,
      H,
      w: track(first.w, ev('w')),
      h: track(first.h, ev('h')),
      r: track(first.r, ev('r')),
      bg: colorTrack(first.bg, ev('bg')),
      cam: track(
        cameraFor(first.w, first.h, W, H, fill, cap),
        rest.map((s) => ({
          t: s.t + 0.04,
          to: cameraFor(s.w, s.h, W, H, fill, cap),
          w: 8,
          z: 0.95
        }))
      ),
      stretch: STRETCH
        ? track(0, [
            { hold: STRETCH.hold, fn: STRETCH.fn, to: 0, w: 15, z: 0.72 }
          ])
        : () => 0,
      cx,
      cy
    }
  }

  let lastT = 0
  const seek = (tRaw) => {
    lastT = tRaw
    // Ohne Größe lässt sich keine Kamera rechnen, der ResizeObserver holt das nach
    if (!root.clientWidth || !root.clientHeight) return
    if (!built || built.W !== root.clientWidth || built.H !== root.clientHeight)
      build()
    const b = built
    const t = ((tRaw % DUR) + DUR) % DUR

    const cam = b.cam(t)
    world.style.transform = `scale(${cam.toFixed(4)})`
    const stretch = b.stretch(t)
    const w = b.w(t) + stretch
    const h = b.h(t)
    shape.style.width = `${w.toFixed(2)}px`
    shape.style.height = `${h.toFixed(2)}px`
    shape.style.borderRadius = `${Math.max(0, b.r(t)).toFixed(2)}px`
    shape.style.backgroundColor = b.bg(t)
    shape.style.transform = `translate(calc(-50% + ${(stretch / 2).toFixed(2)}px), -50%)`
    shape.style.setProperty('--sx', `${(-stretch / 2).toFixed(2)}px`)

    const vis = {}
    Object.entries(content).forEach(([id, node]) => {
      const v = Math.max(0, ...(runs[id] || []).map((r) => presence(t, r)))
      vis[id] = v
      node.style.opacity = v.toFixed(3)
      node.style.visibility = v < 0.002 ? 'hidden' : 'visible'
      node.style.filter =
        v > 0.995 ? 'none' : `blur(${((1 - v) * 10).toFixed(2)}px)`
      node.style.transform = `scale(${(0.97 + 0.03 * v).toFixed(4)})`
    })

    const frame = { t, w, h, cam, stretch }
    updates.forEach(({ ids, update }) => {
      if (ids.some((id) => vis[id] > 0.002)) update(t, frame)
    })

    // Cursor: konstante Größe auf dem Bildschirm, gedrückt bei Klick und Drag
    const held = (CURSOR.holds || []).reduce(
      (acc, [a, c]) =>
        Math.max(
          acc,
          smooth((t - a + 0.06) / 0.08) * (1 - smooth((t - c) / 0.08))
        ),
      0
    )
    const down = Math.max(
      held,
      ...CURSOR.clicks.map((c) => press(t, c - 0.06, 0.24))
    )
    const s = (cursorScale / cam) * (1 - 0.14 * down)
    cursor.style.transform = `translate(${b.cx(t).toFixed(2)}px, ${b.cy(t).toFixed(2)}px) scale(${s.toFixed(4)})`
  }

  const ro = new ResizeObserver(() => seek(lastT))
  ro.observe(root)
  seek.dispose = () => ro.disconnect()
  return seek
}
